import { useState, useEffect } from "react";
import {
  Card,
  Col,
  Row,
  Badge,
  Button,
  Table,
  Form,
  Alert,
} from "react-bootstrap";
import { FaBackward } from "react-icons/fa";
import axios from "axios";
import { getURLbyEndPointV2 } from "../../../../../store/api";
import { PopUpModalButton } from "../../../../../components/basic/ModalComponent";
import { getAuthUserlocal } from "../../../../../store/services";

const ArbitratorDisputesPage = ({ arbitratorId }) => {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDispute, setSelectedDispute] = useState(null);

  useEffect(() => {
    const fetchDisputes = async () => {
      try {
        const response = await axios.get(
          getURLbyEndPointV2("getDisputeDetailArbitrator") + arbitratorId
        );
        setDisputes(response.data.data || []);
      } catch (err) {
        setError(err.message || "Failed to load disputes");
      } finally {
        setLoading(false);
      }
    };

    if (arbitratorId) {
      fetchDisputes();
    } else {
      setError("Arbitrator ID is missing");
      setLoading(false);
    }
  }, [arbitratorId]);

  // === HELPERS ===
  const renderValue = (value) => value || "N/A";

  const formatDate = (date) => {
    if (!date) return "N/A";
    try {
      return new Date(date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const formatCurrency = (amount) =>
    amount && !isNaN(amount)
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(amount)
      : "N/A";

  const getStatusVariant = (status) => {
    switch ((status || "").toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
      case "scheduled":
        return "success";
      case "rejected":
        return "danger";
      case "document verified":
        return "info";
      default:
        return "secondary";
    }
  };

  const getDocName = (key) => {
    const names = {
      "E-C1": "Appointment of Arbitrator",
      "E-C1-Arb1": "Appointment Letter - Arbitrator 1",
      "E-C1-Arb2": "Appointment Letter - Arbitrator 2",
      "E-C1-Arb3": "Appointment Letter - Arbitrator 3",
      "E-C2": "Claimant to Arbitrator",
      "E-C3": "Respondent to Arbitrator",
      "E-R1": "Statement of Defense",
      "E-D1": "Written Claimant Statement (E-D1)",
      "E-D2": "Written Statement (E-D2)",
      "E-G1": "Hearing Notice Of claimant (E-G1)",
      "E-G2": "Hearing Notice Of Respondent (E-G2)",
      "E-G-Arb1": "Hearing Notice - Arbitrator 1",
      "E-G-Arb2": "Hearing Notice - Arbitrator 2",
      "E-G-Arb3": "Hearing Notice - Arbitrator 3",
      ToTribunal: "Hearing Notice - Sole Arbitrator/Tribunal",
      "E-I1": "Checklist to Arbitrators (E-I1)",
      "E-I2": "Feedback Form (E-I2)",
      "E-I2-Claimant": "Feedback Form - Claimant (E-I2-Claimant)",
      "E-I2-Respondent": "Feedback Form - Respondent (E-I2-Respondent)",
      "E-I3": "Copy of Award (in writing) (E-I3)",
      "E-I3-Arb1": "Copy of Award - Arbitrator 1",
      "E-I3-Arb2": "Copy of Award - Arbitrator 2",
      "E-I3-Arb3": "Copy of Award - Arbitrator 3",
      Enquiry: "Enquiry by Arbitrator in Writing",
      CounterClaim: "Counter Claim (by Respondent/Claimant)",
      Production: "Production of Additional Information & Documents",
      CopiesOfAward: "Copies of Award (To Parties)",
      Notices: "Notices reminding parties",
    };
    return names[key] || key;
  };

  const renderFeePlanDetails = () => {
    const sourceData = selectedDispute.paymentSchedule;

    if (!sourceData) return null;

    const feePlan =
      sourceData.selectedFeePlan || sourceData.paymentPlan || "None";

    let flexibleInstallments = sourceData.flexiblePlanInstallments || [];
    let arbitratorSchedules = sourceData.arbitratorFeeDetails?.schedules || [];

    return (
      <>
        {renderValue(feePlan) !== "N/A" && (
          <p className="mb-2">
            <strong>Fee Plan Type:</strong> {feePlan}
          </p>
        )}

        {feePlan === "FLEXIBLE" && flexibleInstallments.length > 0 && (
          <>
            <h6 className="fw-bold mt-4 mb-3">Flexible Plan Installments</h6>
            <Table bordered size="sm" responsive>
              <thead>
                <tr>
                  <th>Installment</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {flexibleInstallments.map((installment, index) => (
                  <tr key={index}>
                    <td>{renderValue(installment.key)}</td>
                    <td>{formatCurrency(installment.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}

        {arbitratorSchedules.length > 0 && (
          <>
            <h6 className="fw-bold mt-4 mb-3">Arbitrator Fee Schedules</h6>
            <Table bordered size="sm" responsive>
              <thead>
                <tr>
                  <th>Schedule</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {arbitratorSchedules.map((schedule, index) => (
                  <tr key={index}>
                    <td>{renderValue(schedule.key)}</td>
                    <td>{formatCurrency(schedule.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </>
    );
  };

  const fileFields = [
    { key: "companyRegistrationDoc", label: "Company Registration Document" },
    { key: "agreementContract", label: "Agreement Contract" },
    { key: "arbitrationForm", label: "Arbitration Form" },
    {
      key: "correspondenceWithSupplier",
      label: "Correspondence with Supplier",
    },
    { key: "commercialInvoice", label: "Commercial Invoice" },
    { key: "inspectionReports", label: "Inspection Reports" },
    { key: "correspondenceWithAgent", label: "Correspondence with Agent" },
    { key: "correspondenceWithCarrier", label: "Correspondence with Carrier" },
    { key: "photographs", label: "Photographs" },
    { key: "legalActionDocuments", label: "Legal Action Documents" },
  ];

  // === RENDER TIMETABLE DOCUMENTS ===
  const renderTimeTableDocuments = (dispute) => {
    if (!dispute.timeTable || !dispute.timeTable.sections) return null;

    // Identify current arbitrator's role index
    const arbIndex = (dispute.appointedArbitratorIds || []).findIndex((arb) => {
      const idToCheck = typeof arb === "object" && arb !== null ? arb._id : arb;
      return String(idToCheck) === String(arbitratorId);
    });

    const sections = dispute.timeTable.sections;
    const allDocs = [];

    sections.forEach((section) => {
      if (section.documents) {
        Object.keys(section.documents).forEach((docKey) => {
          let shouldShow = true;

          // Filter Logic: Hide other arbitrators' specific documents
          // This covers Appointment Letters (E-C1-ArbX) and Awards (E-I3-ArbX) and Notices (E-G-ArbX)
          const isArbSpecific =
            docKey.includes("Arb1") ||
            docKey.includes("Arb2") ||
            docKey.includes("Arb3");

          if (isArbSpecific) {
            if (docKey.includes("Arb1") && arbIndex !== 0) shouldShow = false;
            if (docKey.includes("Arb2") && arbIndex !== 1) shouldShow = false;
            if (docKey.includes("Arb3") && arbIndex !== 2) shouldShow = false;
          }

          // Hide Hearing Notices E-G1 and E-G2 from Arbitrators
          if (docKey === "E-G1" || docKey === "E-G2") {
            shouldShow = false;
          }

          // Hide Feedback Forms (E-I2) from Arbitrator
          if (docKey.startsWith("E-I2")) {
            shouldShow = false;
          }

          if (shouldShow) {
            let displayName = getDocName(docKey);
            // Add a visual indicator for their specific appointment letter
            if (
              (docKey.includes("Arb1") && arbIndex === 0) ||
              (docKey.includes("Arb2") && arbIndex === 1) ||
              (docKey.includes("Arb3") && arbIndex === 2)
            ) {
              displayName += " (Your Copy)";
            }

            allDocs.push({
              section: section.sectionIndex,
              name: displayName,
              url: section.documents[docKey],
              key: docKey,
            });
          }
        });
      }
    });

    if (allDocs.length === 0) return null;

    return (
      <Card className="mb-3">
        <Card.Header>
          <strong>Time Table Documents</strong>
        </Card.Header>
        <Card.Body>
          <Table bordered size="sm" responsive>
            <thead>
              <tr>
                <th>Section</th>
                <th>Document Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {allDocs.map((doc, i) => (
                <tr key={i}>
                  <td>
                    {doc.section === 0 ? "Section A" : `Section ${doc.section}`}
                  </td>
                  <td>{doc.name}</td>
                  <td>
                    <PopUpModalButton title="View" fileLink={doc.url} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
  };

  // === RENDER MEETINGS ===
  const renderMeetingsTable = (dispute) => {
    const allMeetings = (dispute.timeTable?.sections || [])
      .flatMap((s) => s.meetings || [])
      .filter((m) => m && m._id);

    if (allMeetings.length === 0) {
      return (
        <Card className="mb-3">
          <Card.Header>
            <strong>Scheduled Meetings</strong>
          </Card.Header>
          <Card.Body>
            <p className="text-muted mb-0">No meetings scheduled yet.</p>
          </Card.Body>
        </Card>
      );
    }

    const user = getAuthUserlocal();
    const username = user?.firstName
      ? `${user.firstName} (ARBITRATOR)`
      : "Arbitrator";

    return (
      <Card className="mb-3">
        <Card.Header>
          <strong>Scheduled Meetings</strong>
        </Card.Header>
        <Card.Body>
          <div className="table-responsive">
            <Table bordered hover size="sm">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Title</th>
                  <th>Date & Time</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {allMeetings.map((m, idx) => {
                  const joinUrl = `/room/${m.meetingRoomId}?username=${username}`;
                  const dt = (() => {
                    if (!m.meetingDate) return "N/A";
                    const dateStr = new Date(m.meetingDate).toLocaleDateString(
                      "en-US",
                      {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }
                    );

                    const formatTime = (time24) => {
                      if (!time24) return "";
                      const [hours, minutes] = time24.split(":");
                      const hour = parseInt(hours, 10);
                      const ampm = hour >= 12 ? "PM" : "AM";
                      const hour12 = hour % 12 || 12;
                      return `${hour12}:${minutes} ${ampm}`;
                    };

                    return `${dateStr}, ${formatTime(m.meetingTime)}`;
                  })();

                  return (
                    <tr key={m._id}>
                      <td>{idx + 1}</td>
                      <td>{m.meetingTitle || "Untitled Meeting"}</td>
                      <td>{dt}</td>
                      <td>
                        <Badge
                          bg={
                            m.status === "SCHEDULED"
                              ? "primary"
                              : m.status === "ONGOING"
                              ? "warning"
                              : m.status === "COMPLETED"
                              ? "success"
                              : "secondary"
                          }
                        >
                          {m.status}
                        </Badge>
                      </td>
                      <td>
                        <a
                          href={joinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary btn-sm"
                        >
                          Join Meeting
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    );
  };

  // === LOADING / ERROR ===
  if (loading)
    return (
      <Card className="p-4 text-center">
        <Card.Body>Loading...</Card.Body>
      </Card>
    );
  if (error)
    return (
      <Card className="p-4 text-center">
        <Card.Body>Error: {error}</Card.Body>
      </Card>
    );
  if (disputes.length === 0)
    return (
      <Card className="p-4 text-center">
        <Card.Body>No disputes assigned.</Card.Body>
      </Card>
    );

  // === DETAILS VIEW ===
  if (selectedDispute) {
    const isRejected = selectedDispute.isApproved === "Rejected";
    const additionalParties = [
      ...(selectedDispute.claimantOtherPartyIds || []),
      ...(selectedDispute.respondentOtherPartyIds || []),
    ];

    return (
      <div>
        {/* Header */}
        <div className="d-flex gap-2 justify-content-between align-items-center mb-4">
          <h4>Application No: {selectedDispute.appNo}</h4>
          <Button variant="secondary" onClick={() => setSelectedDispute(null)}>
            <FaBackward /> Back
          </Button>
        </div>

        {isRejected && selectedDispute.remark && (
          <Alert variant="danger" className="mb-4">
            <strong>Rejection Reason:</strong> {selectedDispute.remark}
          </Alert>
        )}

        {/* Claimant & Business */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">
                  Claimant Information
                </h5>
                {[
                  "claimantName",
                  "address",
                  "city",
                  "zipCode",
                  "phone",
                  "panNo",
                  "email",
                  "country",
                ].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f.replace(/([A-Z])/g, " $1").trim()}
                    </Form.Label>
                    <div className="form-control-plaintext">
                      {renderValue(selectedDispute[f])}
                    </div>
                  </Form.Group>
                ))}
              </Col>
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">Business Details</h5>
                {[
                  "natureOfBusiness",
                  "industry",
                  "aecciRegNo",
                  "memberType",
                ].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f.replace(/([A-Z])/g, " $1").trim()}
                    </Form.Label>
                    <div className="form-control-plaintext">
                      {renderValue(selectedDispute[f])}
                    </div>
                  </Form.Group>
                ))}
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Respondent & Dispute */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-4">
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">
                  Respondent Information
                </h5>
                {[
                  "respondentCompanyName",
                  "respondentRepresentativeName",
                  "respondentEmail",
                  "respondentAddress",
                  "respondentState",
                  "respondentZipCode",
                  "respondentPanNo",
                  "respondentWebsite",
                  "respondentCountry",
                ].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f
                        .replace("respondent", "")
                        .replace(/([A-Z])/g, " $1")
                        .trim() || "Website"}
                    </Form.Label>
                    <div className="form-control-plaintext">
                      {f === "respondentWebsite" && selectedDispute[f] ? (
                        <a
                          href={selectedDispute[f]}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {selectedDispute[f]}
                        </a>
                      ) : (
                        renderValue(selectedDispute[f])
                      )}
                    </div>
                  </Form.Group>
                ))}
              </Col>
              <Col xs={12} md={6}>
                <h5 className="fw-bold mb-3 text-primary">Dispute Details</h5>
                {["date", "agreementStartDate", "disputeStartDate"].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f.replace(/([A-Z])/g, " $1").trim()}
                    </Form.Label>
                    <div className="form-control-plaintext">
                      {formatDate(selectedDispute[f])}
                    </div>
                  </Form.Group>
                ))}
                {[
                  "place",
                  "product",
                  "commercialInvoiceAmount",
                  "amountOfActivity",
                ].map((f) => (
                  <Form.Group key={f} className="mb-2">
                    <Form.Label className="fs-6 fw-semibold">
                      {f.replace(/([A-Z])/g, " $1").trim()}
                    </Form.Label>
                    <div className="form-control-plaintext">
                      {f.includes("Amount")
                        ? formatCurrency(selectedDispute[f])
                        : renderValue(selectedDispute[f])}
                    </div>
                  </Form.Group>
                ))}
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">
                    Quantum of Claim
                  </Form.Label>
                  <div className="form-control-plaintext">
                    {formatCurrency(selectedDispute.quantumOfClaim)}
                  </div>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="fs-6 fw-semibold">
                    Amount in Dispute
                  </Form.Label>
                  <div className="form-control-plaintext">
                    {formatCurrency(selectedDispute.amountInDispute)}
                  </div>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label className="fs-6 fw-semibold">
                    Dispute Description
                  </Form.Label>
                  <div className="form-control-plaintext">
                    {renderValue(selectedDispute.disputeDescription)}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Additional Parties */}
        {(selectedDispute.claimantOtherPartyIds?.length > 0 ||
          selectedDispute.respondentOtherPartyIds?.length > 0) && (
          <Card className="mb-3">
            <Card.Header>
              <strong>Additional Parties</strong>
            </Card.Header>
            <Card.Body>
              <Table bordered size="sm" responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Assigned To</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Mobile</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedDispute.claimantOtherPartyIds || []).map(
                    (party, idx) => (
                      <tr key={`claimant-party-${idx}`}>
                        <td>Claimant</td>
                        <td>{renderValue(party.name)}</td>
                        <td>{renderValue(party.email)}</td>
                        <td>{renderValue(party.mobile)}</td>
                      </tr>
                    )
                  )}
                  {(selectedDispute.respondentOtherPartyIds || []).map(
                    (party, idx) => (
                      <tr key={`respondent-party-${idx}`}>
                        <td>Respondent</td>
                        <td>{renderValue(party.name)}</td>
                        <td>{renderValue(party.email)}</td>
                        <td>{renderValue(party.mobile)}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Registered Respondent User Separate Card */}
        {selectedDispute.respondentId && (
          <Card className="mb-3">
            <Card.Header>
              <strong>Registered Respondent User</strong>
            </Card.Header>
            <Card.Body>
              <Table bordered size="sm" responsive className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Statement</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {renderValue(
                        `${selectedDispute.respondentId.firstName} ${selectedDispute.respondentId.surName}`
                      )}
                    </td>
                    <td>{renderValue(selectedDispute.respondentId.email)}</td>
                    <td>
                      {selectedDispute.claimantStatement ? (
                        <PopUpModalButton
                          title="View Detail"
                          fileLink={selectedDispute.claimantStatement}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {/* Financial Details */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Financial Details</strong>
          </Card.Header>
          <Card.Body>
            <Row className="g-4 mb-3">
              <Col xs={12} md={6}>
                <p className="mb-2">
                  <strong>Quantum of Claim:</strong>{" "}
                  {formatCurrency(selectedDispute.quantumOfClaim)}
                </p>
              </Col>
              <Col xs={12} md={6}>
                <p className="mb-2">
                  <strong>Amount in Dispute:</strong>{" "}
                  {formatCurrency(selectedDispute.amountInDispute)}
                </p>
              </Col>
            </Row>

            <div className="table-responsive mb-3">
              <Table bordered size="sm">
                <tbody>
                  <tr>
                    <td>Filing Fee</td>
                    <td>
                      {formatCurrency(
                        selectedDispute.paymentSchedule?.filingFee
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Admin Fee</td>
                    <td>
                      {formatCurrency(
                        selectedDispute.paymentSchedule?.adminFee
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Arbitrator Fee (Payable Now)</td>
                    <td>
                      {formatCurrency(
                        selectedDispute.paymentSchedule?.arbitratorFeeDetails
                          ?.payableNow
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>Total Fee</td>
                    <td>
                      {formatCurrency(
                        selectedDispute.paymentSchedule?.totalFee
                      )}
                    </td>
                  </tr>
                </tbody>
              </Table>
            </div>

            {renderFeePlanDetails()}
          </Card.Body>
        </Card>

        {/* Uploaded Documents */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Uploaded Documents</strong>
          </Card.Header>
          <Card.Body>
            <Table bordered size="sm">
              <thead>
                <tr>
                  <th>Document</th>
                  <th>Current</th>
                </tr>
              </thead>
              <tbody>
                {fileFields.map(({ key, label }) => (
                  <tr key={key}>
                    <td>{label}</td>
                    <td>
                      {selectedDispute[key] ? (
                        <PopUpModalButton
                          title="View"
                          fileLink={selectedDispute[key]}
                        />
                      ) : (
                        "Not uploaded"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* TIMETABLE DOCUMENTS */}
        {renderTimeTableDocuments(selectedDispute)}

        {/* Status & Notes */}
        <Card className="mb-3">
          <Card.Header>
            <strong>Status & Notes</strong>
          </Card.Header>
          <Card.Body>
            <p className="mb-2">
              <strong>Status:</strong>{" "}
              <Badge
                bg={getStatusVariant(selectedDispute.status)}
                className="ms-2"
              >
                {selectedDispute.status}
              </Badge>
            </p>
            <p className="mb-2">
              <strong>Approval Status:</strong>{" "}
              {selectedDispute.isApproved || "Pending"}
            </p>
            <Form.Group>
              <Form.Label>
                <strong>Notes</strong>
              </Form.Label>
              <div className="form-control-plaintext">
                {renderValue(selectedDispute.notes)}
              </div>
            </Form.Group>
          </Card.Body>
        </Card>
        {/* Appointed Arbitrators (Read-only section from BindingDisputeDetails) */}
        {selectedDispute.paymentSchedule?.paymentCompletedAt &&
          selectedDispute.refNo && (
            <Card className="mb-3">
              <Card.Header>
                <strong>Appointed Arbitrators</strong>
              </Card.Header>
              <Card.Body>
                {selectedDispute.appointedArbitratorIds &&
                selectedDispute.appointedArbitratorIds.length > 0 ? (
                  <div>
                    <p className="mb-3">
                      <strong>Arbitrator Type:</strong>{" "}
                      {selectedDispute.appointedArbitratorType ||
                        "Not specified"}
                    </p>
                    <div className="list-group">
                      {selectedDispute.appointedArbitratorIds.map(
                        (arbitrator, index) => (
                          <div key={arbitrator._id} className="list-group-item">
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="mb-1">
                                  {arbitrator.companyName}
                                </h6>
                                <small>
                                  {arbitrator.firstName} {arbitrator.surName}
                                </small>
                              </div>
                              <Badge bg="info">Arbitrator {index + 1}</Badge>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted">
                    No arbitrators appointed yet
                  </div>
                )}
              </Card.Body>
            </Card>
          )}

        {/* Additional Parties */}
        {selectedDispute.status !== "Pending" && (
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <strong>Additional Parties</strong>
            </Card.Header>
            <Card.Body>
              {additionalParties.length > 0 ? (
                <Table bordered size="sm" responsive>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Company</th>
                      <th>Country</th>
                      <th>Status</th>
                      <th>docs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalParties.map((p, i) => (
                      <tr key={i}>
                        <td>
                          <strong>{p.partyType}</strong>
                        </td>
                        <td>
                          {renderValue(p.firstName)} {renderValue(p.lastName)}
                        </td>
                        <td>{renderValue(p.companyName)}</td>
                        <td>{renderValue(p.country)}</td>
                        <td>
                          <Badge bg={p.isRegistered ? "success" : "warning"}>
                            {p.isRegistered ? "Registered" : "Unregistered"}
                          </Badge>
                        </td>
                        <td>
                          {p.claimantStatement ? (
                            <PopUpModalButton
                              title="View"
                              fileLink={p.claimantStatement}
                            />
                          ) : (
                            "NA"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div>No additional parties added yet</div>
              )}
            </Card.Body>
          </Card>
        )}

        {/* Scheduled Meetings */}
        {renderMeetingsTable(selectedDispute)}
      </div>
    );
  }

  // === LIST VIEW (Clean Cards) ===
  return (
    <Row>
      {disputes.map((dispute) => (
        <Col key={dispute._id} xs={12} md={6} lg={4} className="mb-4">
          <Card
            className="h-100 border-0 shadow-sm rounded-3"
            style={{
              cursor: "pointer",
              backgroundColor: "#fff",
              padding: "1rem 1.25rem",
            }}
            onClick={() => setSelectedDispute(dispute)}
          >
            <Card.Body className="d-flex flex-column">
              <div className="mb-3 d-flex align-items-center">
                <Badge
                  bg="light"
                  text="dark"
                  className="fw-semibold border px-2 py-1"
                  style={{ fontSize: "0.8rem" }}
                >
                  Case: {dispute.caseId || "N/A"}
                </Badge>
              </div>

              <h5 className="fw-semibold mb-1" style={{ fontSize: "1.05rem" }}>
                {dispute.claimantName}{" "}
                <span className="text-muted fw-normal">vs</span>{" "}
                {dispute.respondentName}
              </h5>

              <div className="mt-2">
                <p className="mb-1">
                  <span className="fw-semibold">App No:</span>{" "}
                  <span className="text-muted">{dispute.appNo}</span>
                </p>
                <p className="mb-1">
                  <span className="fw-semibold">Claim:</span>{" "}
                  <span className="text-muted">
                    {formatCurrency(dispute.quantumOfClaim)}
                  </span>
                </p>
              </div>

              <div className="mt-auto pt-2">
                <p className="text-muted small mb-0">
                  Click to view full details →
                </p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      ))}
    </Row>
  );
};

export default ArbitratorDisputesPage;
